import apiClient from './api.service';

export interface HostProcess {
  id: string;
  tenant_id: string;
  host_id: string;
  pid: number;
  ppid?: number;
  name: string;
  command?: string;
  exe_path?: string;
  username?: string;
  status: 'running' | 'sleeping' | 'stopped' | 'zombie' | 'idle' | 'disk-sleep' | 'unknown';
  cpu_percent: number;
  memory_percent: number;
  memory_rss: number;
  memory_vms: number;
  num_threads: number;
  num_fds?: number;
  num_connections?: number;
  io_read_bytes?: number;
  io_write_bytes?: number;
  nice?: number;
  started_at?: string;
  collected_at: string;
  host?: {
    hostname: string;
  };
}

export interface ProcessStatistics {
  totalProcesses: number;
  runningProcesses: number;
  zombieProcesses: number;
  avgCpuUsage: number;
  avgMemoryUsage: number;
  topCpuProcess: { name: string; cpu: number; hostName: string } | null;
  topMemoryProcess: { name: string; memory: number; hostName: string } | null;
}

export interface ProcessAlertRule {
  id: string;
  tenant_id: string;
  host_id?: string;
  name: string;
  description?: string;
  enabled: boolean;
  rule_type: 'running' | 'not_running' | 'cpu_threshold' | 'memory_threshold' | 'count_min' | 'count_max';
  process_pattern: string;
  match_type: 'exact' | 'contains' | 'regex';
  threshold_value?: number;
  min_count?: number;
  max_count?: number;
  check_interval_seconds: number;
  consecutive_failures: number;
  severity: 'info' | 'warning' | 'error' | 'critical';
  notification_channels?: string[];
  last_triggered_at?: string;
  current_state: 'ok' | 'alerting' | 'pending';
  failure_count: number;
  created_at: string;
  updated_at: string;
}

export interface ProcessQueryOptions {
  sortBy?: 'cpu' | 'memory' | 'name' | 'pid';
  sortOrder?: 'ASC' | 'DESC';
  limit?: number;
  offset?: number;
  search?: string;
  status?: string;
}

export const processApi = {
  /**
   * Get processes for a specific host
   */
  async getHostProcesses(
    hostId: string,
    options?: ProcessQueryOptions
  ): Promise<{ data: HostProcess[]; meta: { total: number; limit: number; offset: number } }> {
    const params = new URLSearchParams();
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.offset) params.append('offset', String(options.offset));
    if (options?.search) params.append('search', options.search);
    if (options?.status) params.append('status', options.status);

    const queryString = params.toString();
    const url = queryString
      ? `/v1/infrastructure/hosts/${hostId}/processes?${queryString}`
      : `/v1/infrastructure/hosts/${hostId}/processes`;
    return apiClient.get(url);
  },

  /**
   * Get process statistics across all hosts
   */
  async getStatistics(): Promise<ProcessStatistics> {
    const result = await apiClient.get('/v1/infrastructure/processes/statistics');
    return result.data;
  },

  /**
   * Get top processes by CPU or memory
   */
  async getTopProcesses(metric: 'cpu' | 'memory' = 'cpu', limit: number = 10): Promise<HostProcess[]> {
    const result = await apiClient.get(`/v1/infrastructure/processes/top?metric=${metric}&limit=${limit}`);
    return result.data;
  },

  /**
   * Get process alert rules
   */
  async getAlertRules(hostId?: string): Promise<ProcessAlertRule[]> {
    const params = hostId ? `?hostId=${hostId}` : '';
    const result = await apiClient.get(`/v1/infrastructure/process-alerts${params}`);
    return result.data;
  },

  /**
   * Create a process alert rule
   */
  async createAlertRule(data: Omit<ProcessAlertRule, 'id' | 'tenant_id' | 'created_at' | 'updated_at' | 'failure_count' | 'current_state'>): Promise<ProcessAlertRule> {
    const result = await apiClient.post('/v1/infrastructure/process-alerts', data);
    return result.data;
  },

  /**
   * Update a process alert rule
   */
  async updateAlertRule(ruleId: string, data: Partial<ProcessAlertRule>): Promise<ProcessAlertRule> {
    const result = await apiClient.put(`/v1/infrastructure/process-alerts/${ruleId}`, data);
    return result.data;
  },

  /**
   * Delete a process alert rule
   */
  async deleteAlertRule(ruleId: string): Promise<void> {
    await apiClient.delete(`/v1/infrastructure/process-alerts/${ruleId}`);
  },

  /**
   * Toggle a process alert rule
   */
  async toggleAlertRule(ruleId: string): Promise<ProcessAlertRule> {
    const result = await apiClient.post(`/v1/infrastructure/process-alerts/${ruleId}/toggle`);
    return result.data;
  },
};

export default processApi;
